<script setup>
import { useForm, usePage } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import { Button } from '@/Components/ui/button'
import { Alert, AlertDescription } from '@/Components/ui/alert'
import { ShieldCheck, AlertCircle, CheckCircle2 } from 'lucide-vue-next'
import { computed } from 'vue'
import { store } from '@/routes/reset-password'

const props = defineProps({
  email: String,
  token: String,
  isNewUser: {
    type: Boolean,
    default: false,
  },
})

const page = usePage()
const form = useForm({
  token: props.token,
  email: props.email,
  password: '',
  password_confirmation: '',
})

const submit = () => {
  form.submit(store())
}

const status = computed(() => page.props.flash.status)
const hasErrors = computed(() => Object.keys(form.errors).length > 0)
const pageTitle = computed(() => props.isNewUser ? 'Password Setup' : 'Reset Password')
const pageDescription = computed(() => props.isNewUser ? 'Set up your password to access your account' : 'Enter your new password below')
</script>

<template>
  <AppLayout>
    <div class="flex items-center justify-center min-h-[calc(100vh-200px)] py-12 px-4 sm:px-6 lg:px-8">
      <div class="w-full max-w-md bg-white rounded-2xl shadow-lg p-10">
        <!-- Header -->
        <div class="mb-8">
          <div class="flex justify-center mb-6">
            <div class="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center text-white">
              <ShieldCheck class="w-6 h-6" />
            </div>
          </div>
          <h1 class="text-2xl font-bold text-gray-900 text-center mb-2">{{ pageTitle }}</h1>
          <p class="text-gray-600 text-center">{{ pageDescription }}</p>
        </div>

        <form @submit.prevent="submit" class="space-y-6">
          <!-- General Error Alert -->
          <Alert v-if="hasErrors" variant="destructive">
            <AlertCircle class="h-4 w-4" />
            <AlertDescription>
              Please correct the errors below.
            </AlertDescription>
          </Alert>

          <!-- Success Status Alert -->
          <Alert v-if="status" class="border-green-500 text-green-700 [&>svg]:text-green-700">
            <CheckCircle2 class="h-4 w-4" />
            <AlertDescription>{{ status }}</AlertDescription>
          </Alert>

          <!-- Email Field -->
          <div>
            <label for="email" class="block text-sm font-semibold text-gray-700 mb-3">
              Email <span class="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              v-model="form.email"
              required
              autofocus
              :class="[
                'w-full px-5 py-4 border-2 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900',
                form.errors.email ? 'border-red-500' : 'border-gray-200'
              ]"
            />
            <p v-if="form.errors.email" class="text-sm text-red-500 mt-1">{{ form.errors.email }}</p>
          </div>

          <!-- Password Field -->
          <div>
            <label for="password" class="block text-sm font-semibold text-gray-700 mb-3">
              New Password <span class="text-red-500">*</span>
            </label>
            <input
              id="password"
              type="password"
              v-model="form.password"
              required
              :class="[
                'w-full px-5 py-4 border-2 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900',
                form.errors.password ? 'border-red-500' : 'border-gray-200'
              ]"
            />
            <p v-if="form.errors.password" class="text-sm text-red-500 mt-1">{{ form.errors.password }}</p>
          </div>

          <!-- Confirm Password Field -->
          <div>
            <label for="password_confirmation" class="block text-sm font-semibold text-gray-700 mb-3">
              Confirm New Password <span class="text-red-500">*</span>
            </label>
            <input
              id="password_confirmation"
              type="password"
              v-model="form.password_confirmation"
              required
              :class="[
                'w-full px-5 py-4 border-2 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900',
                form.errors.password_confirmation ? 'border-red-500' : 'border-gray-200'
              ]"
            />
            <p v-if="form.errors.password_confirmation" class="text-sm text-red-500 mt-1">{{ form.errors.password_confirmation }}</p>
          </div>

          <Button class="w-full bg-indigo-600 hover:bg-indigo-700 h-auto py-4 text-base font-semibold rounded-xl" :disabled="form.processing">
            {{ isNewUser ? 'Set Up Password' : 'Reset Password' }}
          </Button>
        </form>
      </div>
    </div>
  </AppLayout>
</template>
